import { useFocusEffect } from 'expo-router';
import { useCallback, useRef, useState } from 'react';
import { Input, type TamaguiElement, Text, XStack, YStack } from 'tamagui';

/**
 * PSMobileInput — the Indian mobile-number field (atom).
 *
 * A `+91` block, then the 10 digits the user types. Same visual language as
 * PSInput (bordered `$base` box, hairline border, `$brand` on focus, `$error`
 * red) — it's the phone-shaped member of that family.
 *
 * Five things worth reading:
 *
 * 1. WHY IT'S NOT `PSInput type="mobile"` (which no longer exists). We only ever
 *    accept Indian numbers, and the country code used to be an INVISIBLE
 *    assumption: the field took 10 bare digits and `+91` was prepended out of
 *    sight. Showing it is the whole point of this component, and a visible
 *    prefix isn't something PSInput can grow — it would need a prefix slot,
 *    i.e. it would stop being a plain text field and start being configurable.
 *    So the mobile case moved out whole. A consequence worth having: there is
 *    now no way to render a mobile field WITHOUT the country code showing.
 *
 * 2. It's ONE field, not two boxes. The frame owns the single border; the `+91`
 *    is a segment INSIDE it, filled `$backgroundSecondary` and split off by its
 *    own right border. That divider takes the SAME color as the frame, so focus
 *    and error flip the whole object at once (a purple ring with a grey seam
 *    through it would read as two controls again). Error wins over focus, as in
 *    PSInput — a focused invalid field still reads as invalid.
 *
 * 3. Because the frame owns the border, the inner `Input` is `unstyled`: that
 *    variant zeroes Tamagui's own border, web outline and background, so we
 *    aren't fighting a second box (it also skips the `size` variant, which reads
 *    the deliberately-empty `size` token group — see constants/tokens.ts). Two
 *    consequences: the frame pins the geometry (height 44, matching PSInput and
 *    PSButton) and children stretch into it, and we re-add `minWidth={0}`
 *    ourselves — that's Tamagui's own fix for a flex input overflowing its
 *    container, and `unstyled` drops it along with the rest.
 *
 * 4. The dial code is DISPLAY-ONLY. `value`/`onChangeText` are the 10 bare
 *    digits — no `+91`, no spaces — so the draft/API contract is unchanged from
 *    when this was a plain PSInput, and whoever builds the request prepends the
 *    code. The field RESTRICTS input to reach that: non-digits are stripped and
 *    length is capped at 10. That's an affordance (same category as the
 *    phone-pad keyboard), not validation — judging whether the number is VALID
 *    stays with the screen via `error`, which this atom only wears.
 *
 *    Which 10 digits survive depends on HOW they arrived, and that distinction
 *    is the fiddly part of this component:
 *      - Typed one at a time, the value can only exceed 10 by a single appended
 *        digit, and that extra press is simply REFUSED (the field is full).
 *      - Arriving in BULK — pasted, autofilled, or typed over a selection — we
 *        keep the LAST 10. That's what strips a country/trunk code the user
 *        brought along (`+91 98765 43210`, `919876543210`, `098765 43210` all
 *        land on the same 10 digits) without maintaining a list of prefixes to
 *        match. Keeping the FIRST 10 would silently corrupt every one of those.
 *    Note the consequence for `maxLength`: it can't be used for the cap at all
 *    (it truncates a paste from the front, natively, before we see it) — see the
 *    comment on the `Input` below before reaching for it.
 *
 * 5. Focus/blur are forwarded up so the screen can run the house
 *    validate-on-blur / clear-on-focus rhythm (don't nag mid-typing; re-check on
 *    leaving; drop the error when they come back to fix it). Tapping anywhere on
 *    the frame — including the `+91` block — focuses the input, so the inert
 *    prefix never eats a tap.
 */

// Bare Indian mobile number: 10 digits, and the code we show for them. The
// digits are the value; the code is chrome (see point 4).
const MOBILE_MAX_DIGITS = 10;
// Exported because point 4 cuts both ways: whoever prepends the code to build the
// E.164 number for the API must use the SAME code this field displays, or the
// user is shown one country and the server is sent another.
export const DIAL_CODE = '+91';

type PSMobileInputProps = {
  /** The bare 10 digits — no country code, no spaces. */
  value: string;
  onChangeText: (value: string) => void;
  placeholder?: string;
  /**
   * Validation message from the screen. Presence (a non-empty string) flips the
   * whole field into its error look and renders the message below it. The atom
   * doesn't validate — it's told it's invalid.
   */
  error?: string;
  disabled?: boolean;
  /**
   * Focus the field once the SCREEN it lives on is focused (pops the keyboard).
   * For single-input screens where typing is the obvious next action — i.e. the
   * auth flow's first step.
   *
   * We do NOT pass RN's native `autoFocus` down: that fires on mount, which on a
   * pushed/presented screen collides with the routing animation and the focus
   * (and keyboard) silently gets dropped. Instead we focus imperatively from a
   * `useFocusEffect`, which runs after the navigation transition settles — so
   * the keyboard reliably comes up. See the ref/effect below.
   */
  autoFocus?: boolean;
  /** Fired when the field gains focus — the screen clears `error` here. */
  onFocus?: () => void;
  /** Fired when the field loses focus — the screen validates + sets `error` here. */
  onBlur?: () => void;
};

export default function PSMobileInput({
  value,
  onChangeText,
  placeholder,
  error,
  disabled = false,
  autoFocus = false,
  onFocus,
  onBlur,
}: PSMobileInputProps) {
  const [focused, setFocused] = useState(false);
  const hasError = !!error;
  const inputRef = useRef<TamaguiElement>(null);

  // Programmatic autoFocus (see the `autoFocus` prop docs): focus once the
  // screen is focused rather than on mount, so the keyboard doesn't get eaten by
  // the routing animation. useFocusEffect runs after the transition settles;
  // it also re-focuses if the user navigates back to a screen with this field.
  useFocusEffect(
    useCallback(() => {
      if (autoFocus) inputRef.current?.focus();
    }, [autoFocus])
  );

  // Digits only, capped at 10, so the value that reaches the screen is always a
  // clean bare number ready to have the dial code prepended. See point 4 for
  // why bulk input keeps the LAST 10 rather than the first.
  const handleChangeText = (text: string) => {
    const digits = text.replace(/\D/g, '');

    // Under the cap — nothing to decide.
    if (digits.length <= MOBILE_MAX_DIGITS) {
      onChangeText(digits);
      return;
    }

    // Over the cap by exactly one digit appended to what we already hold: a
    // keystroke into an already-full field. REFUSE it — sliding the window here
    // would silently drop the digit they typed first and scroll their number
    // along, which is worse than ignoring the extra press. No onChangeText call
    // is needed to revert the display: RN restores the controlled `value` to the
    // native input once the change event is acknowledged.
    if (digits.length === value.length + 1 && digits.startsWith(value)) {
      return;
    }

    // Bulk arrival — a paste, an autofill, or typing over a selection. Keep the
    // LAST 10 digits: that's what drops a leading country/trunk code (91, +91,
    // 0091, 0) however it happened to be punctuated, without us having to guess
    // at prefixes. iOS/Android phone autofill hands us exactly this shape.
    onChangeText(digits.slice(-MOBILE_MAX_DIGITS));
  };

  // Error wins over focus so an invalid field stays red even while editing.
  // Drives the frame border AND the prefix divider — one object, one color.
  const borderColor = hasError ? '$error' : focused ? '$brand' : '$borderColor';

  return (
    <YStack gap="$xs" alignSelf="stretch">
      {/* The frame: owns the border, the radius and the height; children stretch
          into it. `overflow: hidden` is what makes the prefix fill respect the
          rounded corners. Pressing anywhere here (notably the inert +91 block)
          focuses the real input. */}
      <XStack
        items="stretch"
        height={44}
        rounded="$base"
        overflow="hidden"
        borderWidth="$borderWidth.hairline"
        borderColor={borderColor}
        backgroundColor="$background"
        opacity={disabled ? 0.4 : 1}
        onPress={() => inputRef.current?.focus()}
      >
        {/* The country code — presentational, never focusable. Its right border
            is the divider between the two halves. */}
        <YStack
          justify="center"
          items="center"
          px="$md"
          backgroundColor="$backgroundSecondary"
          borderRightWidth="$borderWidth.hairline"
          borderRightColor={borderColor}
        >
          <Text fontFamily="$body" fontSize="$base" color="$textDark">
            {DIAL_CODE}
          </Text>
        </YStack>

        <Input
          ref={inputRef}
          // The frame is the box (see point 3) — this strips Tamagui's own
          // border/outline/background so there's only one.
          unstyled
          value={value}
          onChangeText={handleChangeText}
          // Track focus for our own border, then forward the moment up so the
          // screen can clear the error on focus / re-validate on blur.

          onFocus={() => {
            setFocused(true);
            onFocus?.();
          }}
          onBlur={() => {
            setFocused(false);
            onBlur?.();
          }}
          placeholder={placeholder}
          placeholderTextColor="$textDarkMuted"
          // `disabled` is the frame's own prop — on native it maps to the
          // TextInput's non-editable state (RN's `editable` isn't in Input's
          // typed surface). The dim is on the frame above, not here.
          disabled={disabled}
          // Deliberately NO `maxLength`. It looks like the obvious cap, but RN
          // enforces it NATIVELY and applies it to pasted text too — truncating
          // from the FRONT. A pasted "+91 98765 43210" would arrive as
          // "+91 98765 " and the number would be mangled before handleChangeText
          // ever ran. The cap lives in the handler instead (see point 4).
          keyboardType="phone-pad"
          autoCapitalize="none"
          // iOS + Android autofill of the user's own number.
          textContentType="telephoneNumber"
          autoComplete="tel"
          // Fills the rest of the row; `minWidth` is the flex-overflow fix
          // `unstyled` drops (see point 3). Vertical size comes from the frame's
          // stretch, so height isn't set twice.
          flex={1}
          minWidth={0}
          px="$md"
          fontFamily="$body"
          fontSize="$base"
          color="$textDark"
        />
      </XStack>

      {hasError && (
        <Text fontFamily="$body" fontSize="$small" color="$error">
          {error}
        </Text>
      )}
    </YStack>
  );
}
